import { Button } from "@/components/ui/button";
import { ChefHat, Calendar } from "lucide-react";

export const HeroSection = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[#FDFBF7]">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-orange-50 to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="space-y-8 animate-fade-in-up">
            {/* Micro phrase */}
            <p className="text-sm tracking-widest uppercase font-semibold text-orange-700">
              Comfort without compromise
            </p>

            {/* Main Headline */}
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight text-gray-800">
              Thoughtfully Cooked.{" "}
              <span className="text-orange-700">Professionally Served.</span>
            </h1>

            {/* Sub-headline */}
            <p className="text-lg md:text-xl leading-relaxed text-gray-600 max-w-xl">
              Corporate catering, intimate gatherings, and large-scale events — crafted with care, precision, and warmth.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                onClick={() => scrollToSection("contact")}
                className="bg-orange-700 hover:bg-orange-800 text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg shadow-orange-700/20 transition-transform hover:scale-105"
                data-testid="hero-quote-btn"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request a Quote
              </Button>
              <Button
                onClick={() => scrollToSection("menus")}
                variant="outline"
                className="border-2 border-orange-700 text-orange-700 hover:bg-orange-50 rounded-full px-8 py-6 text-lg font-medium transition-colors"
                data-testid="hero-menus-btn"
              >
                <ChefHat className="w-5 h-5 mr-2" />
                Explore Menus
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex items-center gap-8 pt-8">
              <div className="text-center">
                <p className="text-3xl font-heading font-bold text-orange-700">500+</p>
                <p className="text-sm text-gray-600">Events Catered</p>
              </div>
              <div className="w-px h-12 bg-stone-300" />
              <div className="text-center">
                <p className="text-3xl font-heading font-bold text-orange-700">50+</p>
                <p className="text-sm text-gray-600">Corporate Clients</p>
              </div>
              <div className="w-px h-12 bg-stone-300" />
              <div className="text-center">
                <p className="text-3xl font-heading font-bold text-orange-700">98%</p>
                <p className="text-sm text-gray-600">Satisfaction</p>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative animate-slide-in-right animation-delay-200">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl shadow-orange-900/20">
              <img
                src="https://images.unsplash.com/photo-1769638913559-c8c6984428fe?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Gourmet catering buffet spread with salads and fruits"
                className="w-full h-full object-cover"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>

            {/* Floating card */}
            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border border-stone-100 animate-fade-in animation-delay-400">
              <p className="font-accent text-lg italic text-gray-600">
                "Food that brings people together"
              </p>
              <div className="w-12 h-1 bg-orange-700 rounded-full mt-3" />
            </div>

            {/* Decorative element */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-orange-100 rounded-full -z-10" />
            <div className="absolute -bottom-8 right-12 w-16 h-16 bg-green-100 rounded-full -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
